// src/components/reports/Reports.jsx
import React, { useState } from 'react';
import { useProjects } from '../../hooks/useProjects';
import { useExpenses } from '../../hooks/useExpenses';
import { reportApi } from '../../api/reportApi';
import LoadingSpinner from '../common/LoadingSpinner';
import BudgetReport from './BudgetReport';
import CompanyReport from './CompanyReport';
import ProjectReport from './ProjectReport';
import {
    DocumentChartBarIcon, BuildingOffice2Icon,
    FolderOpenIcon, CurrencyDollarIcon, ArrowPathIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/formatters';
import { useTranslation } from 'react-i18next';

const REPORT_TYPES = [
    { id: 'company', label: 'Report Aziendale', icon: BuildingOffice2Icon, description: 'Panoramica completa organizzazione', color: 'indigo' },
    { id: 'project', label: 'Report Progetto', icon: FolderOpenIcon, description: 'Analisi dettagliata singolo progetto', color: 'blue' },
    { id: 'budget', label: 'Report Budget', icon: CurrencyDollarIcon, description: 'Analisi finanziaria e varianze', color: 'emerald' },
];

const Reports = () => {
    const { t } = useTranslation();
    const [selectedProject, setSelectedProject] = useState('');
    const [reportType, setReportType] = useState('company');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reportData, setReportData] = useState(null);

    const { projects, loading: projectsLoading } = useProjects({ limit: 100 });
    const { expenses, totalAmount } = useExpenses({ limit: 1000 });

    const handleGenerateReport = async () => {
        if ((reportType === 'project' || reportType === 'budget') && !selectedProject) {
            toast.error('Seleziona un progetto');
            return;
        }

        setLoading(true);
        setError(null);
        setReportData(null);

        try {
            let response;
            if (reportType === 'company') {
                response = await reportApi.getCompanyReport();
            } else if (reportType === 'budget') {
                response = await reportApi.getBudgetReport(selectedProject);
            } else {
                response = await reportApi.getProjectReport(selectedProject);
            }
            setReportData(response?.data);
            toast.success('Report generato!');
        } catch (err) {
            const message = err.response?.data?.message || 'Errore nella generazione del report';
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const needsProject = reportType === 'project' || reportType === 'budget';
    const canGenerate = !loading && (reportType === 'company' || selectedProject);

    if (projectsLoading) return <LoadingSpinner />;

    return (
        <div className="space-y-6">

            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Report e Analisi</h1>
                <p className="text-gray-500 mt-1">Genera report dettagliati e analisi finanziarie</p>
            </div>

            {/* KPI rapidi */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                    { icon: '🏗️', label: 'Progetti', value: projects.length, color: 'blue' },
                    { icon: '💰', label: 'Spese Totali', value: formatCurrency(totalAmount), color: 'emerald' },
                    { icon: '📊', label: 'Transazioni', value: expenses.length, color: 'purple' },
                ].map(kpi => (
                    <div key={kpi.label} className="bg-white rounded-2xl border p-5 flex items-center gap-4">
                        <div className={`w-12 h-12 bg-${kpi.color}-50 rounded-xl flex items-center justify-center text-2xl`}>{kpi.icon}</div>
                        <div>
                            <p className="text-xs text-gray-400 uppercase tracking-wider">{kpi.label}</p>
                            <p className="text-xl font-bold text-gray-900">{kpi.value}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Configurazione report */}
            <div className="bg-white rounded-2xl border p-6">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Tipo di Report</h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {REPORT_TYPES.map((type) => {
                        const Icon = type.icon;
                        const active = reportType === type.id;
                        return (
                            <button
                                key={type.id}
                                onClick={() => { setReportType(type.id); setReportData(null); setError(null); }}
                                className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${active
                                        ? `border-${type.color}-400 bg-${type.color}-50`
                                        : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                                    }`}
                            >
                                <Icon className={`w-6 h-6 mt-0.5 flex-shrink-0 ${active ? `text-${type.color}-600` : 'text-gray-400'}`} />
                                <div>
                                    <p className={`font-semibold text-sm ${active ? `text-${type.color}-700` : 'text-gray-700'}`}>
                                        {type.label}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-0.5">{type.description}</p>
                                </div>
                            </button>
                        );
                    })}
                </div>

                {needsProject && (
                    <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Seleziona Progetto *</label>
                        <select
                            value={selectedProject}
                            onChange={e => setSelectedProject(e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 text-sm"
                        >
                            <option value="">— Scegli un progetto —</option>
                            {projects.map(p => (
                                <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                <button
                    onClick={handleGenerateReport}
                    disabled={!canGenerate}
                    className="mt-5 w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {loading
                        ? <><ArrowPathIcon className="w-5 h-5 animate-spin" /> Generazione in corso...</>
                        : <><DocumentChartBarIcon className="w-5 h-5" /> Genera Report</>
                    }
                </button>

                {error && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
                        <span>⚠️</span> {error}
                    </div>
                )}
            </div>

            {/* Output report */}
            {reportData && (
                <div>
                    {reportType === 'company' && <CompanyReport data={reportData} />}
                    {reportType === 'project' && <ProjectReport data={reportData} />}
                    {reportType === 'budget' && <BudgetReport data={reportData} />}
                </div>
            )}

            {/* Anteprima spese recenti */}
            {!reportData && expenses.length > 0 && (
                <div className="bg-white rounded-2xl border overflow-hidden">
                    <div className="px-6 py-4 border-b bg-gray-50">
                        <h3 className="font-semibold text-gray-900">Spese Recenti</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-100">
                            <thead>
                                <tr className="text-xs text-gray-400 uppercase tracking-wider">
                                    <th className="px-6 py-3 text-left">Riferimento</th>
                                    <th className="px-6 py-3 text-left">Descrizione</th>
                                    <th className="px-6 py-3 text-right">{t('expenses.amount')}</th>
                                    <th className="px-6 py-3 text-left">Data</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {expenses.slice(0, 5).map(expense => (
                                    <tr key={expense.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-3 text-sm font-mono text-gray-700">{expense.reference}</td>
                                        <td className="px-6 py-3 text-sm text-gray-600">{expense.description}</td>
                                        <td className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">{formatCurrency(expense.totalAmount)}</td>
                                        <td className="px-6 py-3 text-sm text-gray-500">{new Date(expense.invoiceDate).toLocaleDateString('it-IT')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="px-6 py-3 bg-gray-50 border-t text-center">
                        <p className="text-sm text-gray-400">Genera un report per l'analisi completa</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Reports;